import { Controller } from "@hotwired/stimulus"
import i18n from "../javascripts/util/i18n"

// Visual expression builder: one operand ("term") per row — a condition (with a
// true/false toggle) or an alias (no value) — joined by an AND/OR connector that
// appears on every row except the first. A single term with no connector is a
// valid expression (e.g. `$c1=1`), so no operator is required.
//
// The public target/action names keep the historical "pair" naming so the
// section forms that reference #addPair / #removePair / the `pairs` target need
// no changes.
export default class extends Controller {
  static targets = [
    "pairs",
    "output",
    "preview",
    "outputWrapper",
    "builder",
    "toggle",
    "manualHint",
    "sourceLine"
  ]

  static values = {
    conditions: Array,
    expression: String
  }

  connect() {
    this.conditions = this.conditionsValue || []
    this.manualMode = false
    this.advanced = false
    this.basicUnavailable = false
    this.hasOperatorValidationError = false

    const initialExpression = (this.expressionValue || "").trim()
    if (initialExpression.length > 0) {
      this.outputTarget.value = initialExpression
      this.previewTarget.textContent = initialExpression
      const parsed = this.parseExpressionToPairs(initialExpression)
      if (parsed) {
        this.renderPairs(parsed)
        this.showBasic()
      } else {
        // Can't be represented as visual rows — open in advanced mode so the
        // saved expression stays editable.
        this.manualMode = true
        this.advanced = true
        this.renderPairs([ this.emptyPair() ])
        this.showAdvanced()
      }
    } else {
      this.renderPairs([ this.emptyPair() ])
      this.syncExpressionFromPairs()
      this.showBasic()
    }

    this.updateManualHint()
    this.bindSubmitValidation()
  }

  disconnect() {
    if (this.formSubmitHandler && this.formElement) {
      this.formElement.removeEventListener("submit", this.formSubmitHandler)
    }
  }

  // Restore the default empty state (basic mode, one blank row). The alias modal
  // is a persistent singleton dialog, so without this it reopens in whatever mode
  // the previous alias left it in.
  reset() {
    this.manualMode = false
    this.advanced = false
    this.basicUnavailable = false
    this.hasOperatorValidationError = false
    this.outputTarget.value = ""
    this.previewTarget.textContent = ""
    this.renderPairs([ this.emptyPair() ])
    this.syncExpressionFromPairs()
    this.showBasic()
    this.updateManualHint()
  }

  addPair(event) {
    event.preventDefault()
    const pairs = this.currentPairs()
    pairs.push(this.emptyPair(pairs.length > 0 ? "AND" : ""))
    this.manualMode = false
    this.renderPairs(pairs)
    this.syncExpressionFromPairs()
    this.updateManualHint()
  }

  removePair(event) {
    event.preventDefault()
    const index = Number(event.currentTarget.dataset.index || -1)
    const pairs = this.currentPairs()
    if (index < 0 || index >= pairs.length) {
      return
    }

    pairs.splice(index, 1)
    if (pairs.length === 0) {
      pairs.push(this.emptyPair())
    }
    if (pairs.length > 0) {
      pairs[0].connector = ""
    }

    this.manualMode = false
    this.renderPairs(pairs)
    this.syncExpressionFromPairs()
    this.updateManualHint()
  }

  onPairChange() {
    this.manualMode = false
    this.syncExpressionFromPairs()
    this.updateManualHint()
  }

  // The condition/alias picker changed — re-render so the value toggle
  // (true/false) shows for conditions and hides for aliases, then resync.
  onSourceChange() {
    this.manualMode = false
    this.renderPairs(this.currentPairs())
    this.syncExpressionFromPairs()
    this.updateManualHint()
  }

  onOutputInput() {
    const expression = this.outputTarget.value.trim()
    this.previewTarget.textContent = expression

    if (expression.length === 0) {
      this.manualMode = false
      this.renderPairs([ this.emptyPair() ])
      this.updateManualHint()
      return
    }

    const parsed = this.parseExpressionToPairs(expression)
    if (parsed) {
      this.manualMode = false
      this.renderPairs(parsed)
    } else {
      this.manualMode = true
    }
    this.updateManualHint()
  }

  // Switch between basic (visual row builder) and advanced (type the raw
  // expression) modes.
  toggleMode(event) {
    if (event) {
      event.preventDefault()
    }

    if (this.advanced) {
      // advanced -> basic: parse the typed expression back into rows.
      const expression = (this.outputTarget.value || "").trim()
      const parsed = expression.length ? this.parseExpressionToPairs(expression) : [ this.emptyPair() ]
      if (!parsed) {
        // Too complex to represent visually — stay in advanced mode and warn.
        this.basicUnavailable = true
        this.updateManualHint()
        return
      }
      this.basicUnavailable = false
      this.advanced = false
      this.manualMode = false
      this.renderPairs(parsed)
      this.syncExpressionFromPairs()
      this.showBasic()
    } else {
      // basic -> advanced: make sure the field reflects the current rows.
      this.syncExpressionFromPairs()
      this.basicUnavailable = false
      this.advanced = true
      this.manualMode = true
      this.showAdvanced()
      this.outputTarget.focus()
    }

    this.updateManualHint()
  }

  showBasic() {
    if (this.hasBuilderTarget) {
      this.builderTarget.classList.remove("hidden")
    }
    this.outputWrapperTarget.classList.add("hidden")
    // The "Source:" preview is useful alongside the row builder; in advanced
    // mode it just duplicates the field above it.
    if (this.hasSourceLineTarget) {
      this.sourceLineTarget.classList.remove("hidden")
    }
    this.toggleTarget.textContent = i18n.t("js.pair_expression_builder.advanced_mode")
  }

  showAdvanced() {
    if (this.hasBuilderTarget) {
      this.builderTarget.classList.add("hidden")
    }
    this.outputWrapperTarget.classList.remove("hidden")
    if (this.hasSourceLineTarget) {
      this.sourceLineTarget.classList.add("hidden")
    }
    this.toggleTarget.textContent = i18n.t("js.pair_expression_builder.basic_mode")
  }

  syncExpressionFromPairs() {
    const pairs = this.currentPairs()
    this.clearOperatorInvalidState()
    this.hasOperatorValidationError = false

    // Only rows with a selected source contribute; every such row after the
    // first needs a valid AND/OR connector. A single term needs no operator.
    const keyed = pairs.filter((pair) => pair.key)
    keyed.slice(1).forEach((pair) => {
      if (!this.isValidOperator(pair.connector)) {
        this.hasOperatorValidationError = true
        this.markPairOperatorInvalid(pair.index)
      }
    })

    if (this.hasOperatorValidationError) {
      this.outputTarget.value = ""
      this.previewTarget.textContent = ""
      return
    }

    const expression = keyed
      .map((pair, index) => {
        const token = this.buildExpressionToken(pair.key, pair.value, pair.sourceType)
        return index === 0 ? token : `${pair.connector} ${token}`
      })
      .join(" ")
      .trim()

    this.outputTarget.value = expression
    this.previewTarget.textContent = expression
  }

  currentPairs() {
    const pairs = []
    this.pairsTarget.querySelectorAll("[data-pair-row='true']").forEach((pairEl, index) => {
      const connectorInput = pairEl.querySelector("[data-role='connector']")
      const key = this.slotKey(pairEl)

      pairs.push({
        index,
        connector: index === 0 ? "" : this.normalizeOperator(connectorInput?.value),
        key,
        sourceType: this.sourceTypeForKey(key),
        value: pairEl.querySelector("[data-role='term-value']")?.value === "0" ? "0" : "1"
      })
    })
    return pairs
  }

  // Reads the picker select for a row; the "__none__" sentinel clears it.
  slotKey(pairEl) {
    const value = String(pairEl.querySelector("[data-role='term-input']")?.value || "").trim()
    return value === "__none__" ? "" : value
  }

  renderPairs(pairs) {
    this.pairsTarget.innerHTML = pairs
      .map((pair, index) => this.pairTemplate(pair, index, pairs.length))
      .join("")
  }

  pairTemplate(pair, index, length) {
    const connectorSelect = index === 0
      ? `<div class="w-[4.5rem]"></div>`
      : this.operatorSelect("connector", pair.connector)

    const key = pair.key
    const value = pair.value === "0" ? "0" : "1"
    const sourceType = key ? this.sourceTypeForKey(key) : "condition"
    // The true/false toggle only applies to conditions; aliases carry no value.
    const showValue = Boolean(key) && sourceType !== "alias"

    return `
      <div data-pair-row="true" class="flex flex-wrap items-center gap-2">
        ${connectorSelect}
        <div class="flex flex-1 min-w-[10rem] items-center gap-2">
          ${this.sourceSelect(key)}
          ${showValue ? `<div data-role="term-value-wrap">${this.valueToggle(value)}</div>` : ""}
        </div>
        <button type="button"
                data-index="${index}"
                data-action="click->pair-expression-builder#removePair"
                title="${this.escapeHtml(i18n.t("js.pair_expression_builder.remove_expression"))}"
                aria-label="${this.escapeHtml(i18n.t("js.pair_expression_builder.remove_expression"))}"
                class="inline-flex cursor-pointer items-center justify-center rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 shadow-theme-xs transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-200 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                ${length <= 1 ? "disabled" : ""}>
          <i class="fa-solid fa-trash" aria-hidden="true"></i>
        </button>
      </div>
    `
  }

  // AND/OR operator picker rendered with the shared searchable-select component
  // (auto-connected by Stimulus). The hidden <select> keeps the data-role +
  // change action the builder reads; AND is the default.
  //
  // NOTE: operatorSelect/sourceSelect reproduce the markup of the shared
  // backoffice/shared/_searchable_select partial because these selects are built
  // dynamically per row in JS. Keep them in sync if that partial's markup changes.
  operatorSelect(role, value) {
    const current = this.normalizeOperator(value) || "AND"
    const options = [ [ "AND", "AND" ], [ "OR", "OR" ] ]
      .map(([ optionValue, label ]) =>
        `<option value="${optionValue}" ${optionValue === current ? "selected" : ""}>${label}</option>`)
      .join("")

    return `
      <div class="group/ss relative w-[4.5rem]" data-controller="searchable-select" data-searchable-select-placeholder-value="${this.escapeHtml(i18n.t("js.pair_expression_builder.operator_placeholder"))}" data-searchable-select-no-matches="${this.escapeHtml(i18n.t("js.common.no_matches"))}">
        <select hidden data-role="${role}" data-searchable-select-target="select" data-action="change->pair-expression-builder#onPairChange">${options}</select>
        <button type="button" data-searchable-select-target="button" data-action="searchable-select#toggle" class="flex w-full items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white pl-3 pr-1 py-2 text-left text-sm text-gray-800 shadow-theme-xs transition-colors hover:border-gray-400 group-focus-within/ss:!border-brand-500 group-focus-within/ss:ring-2 group-focus-within/ss:ring-brand-200 dark:group-focus-within/ss:ring-brand-500/30 focus:outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:border-gray-600">
          <span data-searchable-select-target="label" class="truncate"></span>
          <svg class="h-4 w-4 shrink-0 text-gray-400" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M6 8L10 12L14 8" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <div data-searchable-select-target="panel" hidden class="absolute z-20 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900">
          <div data-searchable-select-target="list" class="max-h-56 overflow-y-auto py-1"></div>
        </div>
      </div>
    `
  }

  // Searchable condition/alias picker, rendered with the shared searchable-select
  // component. The blank option is the unselected/placeholder state.
  sourceSelect(key) {
    const options = [
      `<option value="" ${key ? "" : "selected"}></option>`
    ].concat((this.conditions || []).map((source) => {
      const optionKey = this.escapeHtml(String(source.key || ""))
      const label = this.escapeHtml(`${source.title || source.key} (${source.key})`)
      const selected = String(source.key) === String(key) ? "selected" : ""
      return `<option value="${optionKey}" ${selected}>${label}</option>`
    })).join("")

    return `
      <div class="group/ss relative flex-1 min-w-0" data-controller="searchable-select" data-searchable-select-placeholder-value="${this.escapeHtml(i18n.t("js.pair_expression_builder.source_placeholder"))}" data-searchable-select-no-matches="${this.escapeHtml(i18n.t("js.common.no_matches"))}">
        <select hidden data-role="term-input" data-searchable-select-target="select" data-action="change->pair-expression-builder#onSourceChange">${options}</select>
        <button type="button" data-searchable-select-target="button" data-action="searchable-select#toggle" class="flex w-full items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white pl-3 pr-1 py-2 text-left text-sm text-gray-800 shadow-theme-xs transition-colors hover:border-gray-400 group-focus-within/ss:!border-brand-500 group-focus-within/ss:ring-2 group-focus-within/ss:ring-brand-200 dark:group-focus-within/ss:ring-brand-500/30 focus:outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:border-gray-600">
          <span data-searchable-select-target="label" class="truncate"></span>
          <svg class="h-4 w-4 shrink-0 text-gray-400" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M6 8L10 12L14 8" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <div data-searchable-select-target="panel" hidden class="absolute z-40 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900">
          <div class="border-b border-gray-100 p-1 dark:border-gray-800">
            <input type="text" data-searchable-select-target="search" data-action="input->searchable-select#filter" placeholder="${this.escapeHtml(i18n.t("js.common.search"))}" class="w-full border-0 bg-transparent px-2 py-1.5 text-sm text-gray-800 shadow-none focus:border-0 focus:shadow-none focus:ring-0 focus:ring-offset-0 focus:outline-none dark:text-gray-100" />
          </div>
          <div data-searchable-select-target="list" class="max-h-56 overflow-y-auto py-1"></div>
        </div>
      </div>
    `
  }

  // True/false toggle for a selected condition. A click flips it; the value
  // lives on the button's `value` so currentPairs reads it like a field. Same
  // height/radius as the select boxes, colored green (true) / red (false).
  valueToggle(value) {
    const current = value === "0" ? "0" : "1"
    return `
      <button type="button"
              data-role="term-value"
              value="${current}"
              title="${this.escapeHtml(i18n.t("js.pair_expression_builder.toggle_value_hint"))}"
              data-action="click->pair-expression-builder#toggleValue"
              class="inline-flex w-16 items-center justify-center rounded-lg border px-3 py-2 text-sm font-medium shadow-theme-xs ${this.valueClass(current)}">
        ${this.valueLabel(current)}
      </button>
    `
  }

  valueLabel(value) {
    return i18n.t(value === "0" ? "js.pair_expression_builder.value_false" : "js.pair_expression_builder.value_true")
  }

  valueClass(value) {
    return value === "0"
      ? "border-red-200 bg-red-50 text-red-700 hover:bg-red-100 dark:border-red-700/60 dark:bg-red-900/30 dark:text-red-200 dark:hover:bg-red-900/50"
      : "border-green-200 bg-green-50 text-green-700 hover:bg-green-100 dark:border-green-700/60 dark:bg-green-900/30 dark:text-green-200 dark:hover:bg-green-900/50"
  }

  toggleValue(event) {
    event.preventDefault()
    const button = event.currentTarget
    const next = button.value === "0" ? "1" : "0"
    button.value = next
    button.textContent = this.valueLabel(next)
    button.className = `inline-flex w-16 items-center justify-center rounded-lg border px-3 py-2 text-sm font-medium shadow-theme-xs ${this.valueClass(next)}`
    this.onPairChange()
  }

  parseExpressionToPairs(expression) {
    const normalized = String(expression || "").trim()
    if (!normalized.length) {
      return [ this.emptyPair() ]
    }

    // A flat one-term-per-row builder can't express operator grouping. Anything
    // with meaningful parentheses (beyond a single wrap around the whole
    // expression) stays in advanced mode so its exact semantics are preserved
    // rather than silently re-flattened.
    const stripped = this.stripOuterParens(normalized)
    if (stripped.includes("(") || stripped.includes(")")) {
      return null
    }

    const clauses = []
    const tokenRegex = /\$?([a-z]+\d+)(?:\s*=\s*([01]))?/ig
    let clauseMatch = tokenRegex.exec(stripped)
    while (clauseMatch) {
      const key = String(clauseMatch[1] || "").toLowerCase()
      const source = this.findSource(key)
      // References a key the picker doesn't offer (removed/renamed condition, or
      // one from another scope). It can't be shown as a row without silently
      // dropping it on save, so keep the whole expression in advanced mode.
      if (!source) {
        return null
      }
      const sourceType = this.sourceTypeFor(source)
      const hasValue = typeof clauseMatch[2] !== "undefined"
      if (sourceType === "alias" && hasValue) {
        return null
      }
      if (sourceType !== "alias" && !hasValue) {
        return null
      }
      clauses.push({
        key,
        sourceType,
        value: String(clauseMatch[2] || "1") === "0" ? "0" : "1"
      })
      clauseMatch = tokenRegex.exec(stripped)
    }

    const operators = stripped.match(/\b(AND|OR)\b/ig) || []
    const cleaned = stripped
      .replace(/\$?([a-z]+\d+)(?:\s*=\s*([01]))?/ig, "")
      .replace(/\b(AND|OR)\b/ig, "")
      .replace(/\s+/g, "")

    if (!clauses.length || cleaned.length > 0) {
      return null
    }
    if (clauses.length > 1 && operators.length !== clauses.length - 1) {
      return null
    }

    // One clause per row; the operator before each clause becomes its connector.
    return clauses.map((clause, index) => ({
      connector: index === 0 ? "" : String(operators[index - 1] || "AND").toUpperCase(),
      key: clause.key,
      sourceType: clause.sourceType,
      value: clause.value
    }))
  }

  // Removes redundant parentheses that wrap the entire expression, e.g.
  // "($c1=1 AND $c2=0)" -> "$c1=1 AND $c2=0" (the shape the old builder saved for
  // two-term expressions). Inner grouping is left intact so the caller can detect
  // it and fall back to advanced mode.
  stripOuterParens(expression) {
    let value = String(expression || "").trim()

    while (value.startsWith("(") && value.endsWith(")")) {
      let depth = 0
      let wrapsWhole = true
      for (let i = 0; i < value.length; i++) {
        if (value[i] === "(") {
          depth += 1
        } else if (value[i] === ")") {
          depth -= 1
          if (depth === 0 && i < value.length - 1) {
            wrapsWhole = false
            break
          }
        }
      }
      if (!wrapsWhole || depth !== 0) {
        break
      }
      value = value.slice(1, -1).trim()
    }

    return value
  }

  emptyPair(connector = "") {
    return {
      connector,
      key: "",
      sourceType: "condition",
      value: "1"
    }
  }

  normalizeOperator(value) {
    const normalized = String(value || "").toUpperCase().trim()
    if (normalized === "AND" || normalized === "OR") {
      return normalized
    }

    return ""
  }

  sourceTypeFor(source) {
    return String(source?.source_type || source?.sourceType || "condition")
  }

  sourceTypeForKey(key) {
    return this.sourceTypeFor(this.findSource(key))
  }

  findSource(key) {
    return (this.conditions || []).find(
      (source) => String(source.key || "").toLowerCase() === String(key || "").toLowerCase()
    )
  }

  buildExpressionToken(key, value, sourceType) {
    if (!key) {
      return ""
    }

    if (sourceType === "alias") {
      return `$${key}`
    }

    return `$${key}=${value}`
  }

  isValidOperator(value) {
    return value === "AND" || value === "OR"
  }

  markPairOperatorInvalid(index) {
    const rowEl = this.pairsTarget.querySelectorAll("[data-pair-row='true']")[index]
    const input = rowEl?.querySelector("[data-role='connector']")
    if (!input) {
      return
    }
    // The native <select> is hidden behind the searchable-select button — mark
    // the visible button instead.
    this.operatorControl(input).classList.add("border-red-500", "ring-1", "ring-red-200")
  }

  clearOperatorInvalidState() {
    this.pairsTarget.querySelectorAll("[data-role='connector']").forEach((input) => {
      this.operatorControl(input).classList.remove("border-red-500", "ring-1", "ring-red-200")
    })
  }

  operatorControl(select) {
    return select.closest("[data-controller='searchable-select']")
      ?.querySelector("[data-searchable-select-target='button']") || select
  }

  bindSubmitValidation() {
    this.formElement = this.element.closest("form")
    if (!this.formElement) {
      return
    }

    this.formSubmitHandler = (event) => {
      if (this.manualMode) {
        return
      }

      this.syncExpressionFromPairs()
      if (!this.hasOperatorValidationError) {
        return
      }

      event.preventDefault()
      event.stopPropagation()
    }

    this.formElement.addEventListener("submit", this.formSubmitHandler)
  }

  updateManualHint() {
    if (!this.hasManualHintTarget) {
      return
    }

    // Only shown when the user tried to switch to basic mode but the expression
    // is too complex to represent as rows.
    if (this.basicUnavailable) {
      this.manualHintTarget.classList.remove("hidden")
    } else {
      this.manualHintTarget.classList.add("hidden")
    }
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;")
  }
}
